/**
 * @file Object Editor for schema-based object editing (list mode)
 * @version 2.0.0
 */

import BaseEditor, { DEFAULTS_KEY } from './BaseEditor.js';
import { CONTROLS_TEMPLATE } from './PrimitiveEditor.js';
import { applySchemaCustomizations, getSchemaDefaultValue } from './schemas.js';
import { registerEditor } from './component-registry.js';

const ObjectEditor = {
  name: 'ObjectEditor',
  extends: BaseEditor,

  setup(props, ctx) {
    const base = BaseEditor.setup(props, ctx);

    // Provide enhanced defaults for children (labelWidth for list mode)
    Vue.provide(
      DEFAULTS_KEY,
      Vue.computed(() => ({
        displayMode: 'grid-horizontal',
        labelWidth: '120px',
        ...base.mergedDefaults.value,
      }))
    );

    return base;
  },

  props: {
    schema: {
      type: Object,
      required: true,
      validator(schema) {
        return schema && schema.type === 'object' && schema.properties;
      },
    },
    value: {
      type: Object,
    },
    reference: {
      type: Object,
    },
    fields: {
      type: Array,
      default: null,
    },
    excludeFields: {
      type: Array,
      default: () => [],
    },
    displayFieldsLabels: {
      type: Boolean,
      default: undefined,
    },
    allowPrimaryKeyEdit: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Override customizedSchema to handle primary key
     */
    customizedSchema() {
      const customize = { ...this.customize };
      if (this.schema.primaryKey && !this.allowPrimaryKeyEdit) {
        customize[this.schema.primaryKey] =
          customize[this.schema.primaryKey] ?? {};
        customize[this.schema.primaryKey].editable =
          customize[this.schema.primaryKey].editable ?? false;
      }
      return applySchemaCustomizations(this.schema, customize);
    },

    /**
     * Filtered field names
     */
    fieldNames() {
      const allFields = Object.keys(this.customizedSchema.properties || {});

      if (this.fields) {
        return this.fields.filter(field => allFields.includes(field));
      }

      return allFields.filter(field => !this.excludeFields.includes(field));
    },

    /**
     * Attributes for field labels
     */
    fieldsAttrs() {
      const displayFieldsLabels = this.getAttribute('displayFieldsLabels');
      return displayFieldsLabels !== undefined
        ? { displayLabel: displayFieldsLabels }
        : {};
    },

    /**
     * Add label for object
     */
    addLabel() {
      return this.getAttribute('displayButtonsLabels') ? 'Add value' : '';
    },

    /**
     * External state
     */
    externalState() {
      const state = {};

      if (this.hasErrors) {
        state.state = 'error';
      } else if (this.internalValue.isModified) {
        state.state = 'updated';
        state.update = () => this.internalValue.save();
      } else {
        state.state = 'no_change';
      }

      Object.defineProperty(state, 'sender', {
        value: this,
        enumerable: false,
        writable: false,
      });

      return state;
    },
  },

  methods: {
    /**
     * Get field value
     * @param fieldName
     */
    getFieldValue(fieldName) {
      return this.internalValue.editable[fieldName];
    },

    /**
     * Get field reference
     * @param fieldName
     */
    getFieldReference(fieldName) {
      return this.reference === undefined
        ? undefined
        : (this.reference?.[fieldName] ?? null);
    },

    /**
     * Get field schema
     * @param fieldName
     */
    getFieldSchema(fieldName) {
      let schema = this.customizedSchema.properties[fieldName] || {};
      if (!('title' in schema)) {
        schema = { ...schema, title: fieldName };
      }
      return schema;
    },

    /**
     * Check if field is deletable
     * @param fieldName
     */
    isFieldDeletable(fieldName) {
      const requiredFields = this.customizedSchema.required || [];
      return !requiredFields.includes(fieldName);
    },

    /**
     * Handle field input
     * @param fieldName
     * @param fieldState
     */
    handleFieldInput(fieldName, fieldState) {
      this.childStates.delete(fieldName);
      if (fieldState.state === 'updated' && fieldState.update) {
        const doc = this.internalValue.editable;
        doc[fieldName] = fieldState.update(doc[fieldName]);
      }
      if (fieldState.state === 'error') {
        this.childStates.set(fieldName, fieldState);
      }
    },
  },

  template: `
    <div class="d-flex flex-column gap-2" v-if="canRead" v-bind="$attrs">
      <!-- Header with title and controls -->
      <div class="d-flex align-items-center gap-1" :style="controlStyles">
        <h6 v-if="getAttribute('displayLabel', true) && editorTitle" class="mb-0 me-auto fw-bold">
          {{ editorTitle }}
          <span v-if="isUndefined" class="text-muted fst-italic fw-normal ms-2">—</span>
        </h6>
        <div v-if="!getAttribute('displayLabel', true) || !editorTitle" class="me-auto"></div>

        <div
          v-if="hasErrors && (mode === 'edit' || (mode === 'click-edit' && isEditing))"
          class="invalid-feedback d-block mt-1"
        >
          {{ validationError }}
        </div>

        <div class="d-flex gap-1 align-items-center">
          ${CONTROLS_TEMPLATE}
        </div>
      </div>

      <!-- Fields -->
      <template v-if="!isUndefined">
        <slot
          v-for="fieldName in fieldNames"
          :key="fieldName"
          :name="fieldName"
          :schema="getFieldSchema(fieldName)"
          :value="getFieldValue(fieldName)"
          :reference="getFieldReference(fieldName)"
          :mode="childMode"
          :update="(state) => handleFieldInput(fieldName, state)"
        >
          <div
            :class="(getFieldSchema(fieldName).type === 'object' || getFieldSchema(fieldName).type === 'array') ? 'border-start border-2 ps-3' : ''"
            :style="(getFieldSchema(fieldName).type === 'object' || getFieldSchema(fieldName).type === 'array') ? 'border-color: #dee2e6 !important' : ''"
            class="field-item"
          >
            <component
              :is="getItemComponent(getFieldSchema(fieldName))"
              :schema="getFieldSchema(fieldName)"
              :value="getFieldValue(fieldName)"
              :reference="getFieldReference(fieldName)"
              :mode="childMode"
              :deletable="isFieldDeletable(fieldName)"
              v-bind="fieldsAttrs"
              @input="(state) => handleFieldInput(fieldName, state)"
            />
          </div>
        </slot>
      </template>
    </div>
  `,
};

registerEditor('object', 'default', ObjectEditor);
registerEditor('object', 'list', ObjectEditor);

export default ObjectEditor;
